import React, { useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";

export default function VoiceInput({ onResult }) {
  const { lang, t } = useLang();
  const [state, setState] = useState("idle"); // idle | recording | transcribing
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((tr) => tr.stop());
        setState("transcribing");
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const form = new FormData();
        form.append("file", blob, "audio.webm");
        form.append("language", lang);
        try {
          const { data } = await api.post("/asr", form, { headers: { "Content-Type": "multipart/form-data" } });
          if (data.transcript) onResult(data.transcript.trim());
          else toast.error("No speech detected");
        } catch {
          toast.error("Transcription failed");
        } finally {
          setState("idle");
        }
      };
      mediaRef.current = rec;
      rec.start();
      setState("recording");
    } catch {
      toast.error("Microphone permission denied");
    }
  };

  const stop = () => { if (mediaRef.current && state === "recording") mediaRef.current.stop(); };

  const cls = state === "recording"
    ? "bg-rose-500 text-white animate-pulse"
    : "bg-primary/10 text-primary hover:bg-primary/20";

  return (
    <button type="button" data-testid="voice-input-btn"
      onClick={state === "recording" ? stop : (state === "idle" ? start : undefined)}
      title={state === "recording" ? t("listening") : t("voice_ask")}
      className={`inline-flex items-center justify-center h-11 w-11 rounded-xl transition-colors shrink-0 ${cls}`}>
      {state === "transcribing" ? <Loader2 className="h-5 w-5 animate-spin" /> : state === "recording" ? <Square className="h-4 w-4" /> : <Mic className="h-5 w-5" />}
    </button>
  );
}
